import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, CheckCircle, MapPin, Edit, UserCog, AlertTriangle, Merge, RefreshCw } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export const CitiesPage = () => {
  const [cities, setCities] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [allAdminsForOwnership, setAllAdminsForOwnership] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCity, setShowAddCity] = useState(false);
  const [newCity, setNewCity] = useState({ name: '', state: '', country: 'India', is_active: true });
  const [editCity, setEditCity] = useState(null);
  const [assignDialog, setAssignDialog] = useState({ open: false, admin: null, selectedCities: [] });
  const [ownerDialog, setOwnerDialog] = useState({ open: false, city: null, newOwnerId: '' });
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [merging, setMerging] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      // Fetch cities first - this is the main data
      const citiesRes = await axios.get(`${API}/cities/all`);
      setCities(citiesRes.data || []);
      
      // Try to fetch admins list - use fallback if not available
      try {
        const allAdminsRes = await axios.get(`${API}/superadmin/all-admins-list`);
        setAllAdminsForOwnership(allAdminsRes.data || []);
        setAdmins(allAdminsRes.data?.filter(a => a.role === 'admin') || []);
      } catch (adminErr) {
        console.error("Failed to load admins list:", adminErr);
        // Fallback: try to get admins from users endpoint
        try {
          const usersRes = await axios.get(`${API}/users?role=admin`);
          setAdmins(usersRes.data || []);
          setAllAdminsForOwnership(usersRes.data || []);
        } catch {
          setAdmins([]);
          setAllAdminsForOwnership([]);
        }
      }
    } catch (err) {
      console.error("Failed to load cities:", err);
      toast.error("Failed to load cities data");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeOwner = async () => {
    if (!ownerDialog.city || !ownerDialog.newOwnerId) {
      toast.error("Please select a new owner");
      return;
    }
    try {
      await axios.put(`${API}/superadmin/cities/${ownerDialog.city.id}/change-owner`, {
        new_owner_id: ownerDialog.newOwnerId
      });
      toast.success(`City "${ownerDialog.city.name}" ownership transferred successfully`);
      setOwnerDialog({ open: false, city: null, newOwnerId: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change owner");
    }
  };

  const getOwnerName = (ownerId) => {
    if (!ownerId) return "No Owner";
    const owner = allAdminsForOwnership.find(a => a.id === ownerId);
    return owner ? owner.name : "Unknown";
  };

  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await axios.get(`${API}/superadmin/cities/duplicates`);
      setDuplicates(res.data?.duplicates || []);
      if (res.data?.duplicates?.length > 0) {
        setShowDuplicates(true);
      }
    } catch (err) {
      console.error("Failed to fetch duplicates:", err);
      toast.error("Failed to check for duplicates");
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleMergeCities = async (duplicateGroup) => {
    // Sort by created_at to keep the oldest one
    const sortedCities = [...duplicateGroup.cities].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    
    const keepCity = sortedCities[0];
    const deleteCities = sortedCities.slice(1);
    
    if (!confirm(`Merge ${duplicateGroup.count} "${duplicateGroup._id}" cities?\n\nKeeping: ${keepCity.name} (created ${new Date(keepCity.created_at).toLocaleDateString()})\nDeleting: ${deleteCities.length} duplicate(s)\n\nThis will reassign all orders and drivers to the kept city.`)) {
      return;
    }
    
    setMerging(duplicateGroup._id);
    try {
      const res = await axios.post(`${API}/superadmin/cities/merge`, {
        keep_city_id: keepCity.id,
        delete_city_ids: deleteCities.map(c => c.id)
      });
      
      toast.success(`Merged ${deleteCities.length} duplicate "${duplicateGroup._id}" cities`);
      
      // Refresh data
      fetchData();
      fetchDuplicates();
    } catch (err) {
      console.error("Merge failed:", err);
      toast.error(err.response?.data?.detail || "Failed to merge cities");
    } finally {
      setMerging(null);
    }
  };

  const handleMergeAll = async () => {
    if (!confirm(`Merge ALL ${duplicates.length} duplicate groups?\n\nThis will keep the oldest city in each group and merge all orders/drivers.`)) {
      return;
    }
    
    setMerging('all');
    let merged = 0;
    let failed = 0;
    
    for (const dup of duplicates) {
      try {
        const sortedCities = [...dup.cities].sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        );
        const keepCity = sortedCities[0];
        const deleteCities = sortedCities.slice(1);
        
        await axios.post(`${API}/superadmin/cities/merge`, {
          keep_city_id: keepCity.id,
          delete_city_ids: deleteCities.map(c => c.id)
        });
        merged++;
      } catch {
        failed++;
      }
    }
    
    toast.success(`Merged ${merged} duplicate groups${failed > 0 ? `, ${failed} failed` : ''}`);
    setMerging(null);
    fetchData();
    fetchDuplicates();
  };

  const handleAddCity = async () => {
    if (!newCity.name || !newCity.state) {
      toast.error("City name and state are required");
      return;
    }
    try {
      await axios.post(`${API}/superadmin/cities`, newCity);
      toast.success("City added successfully");
      setShowAddCity(false);
      setNewCity({ name: '', state: '', country: 'India', is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add city");
    }
  };

  const handleUpdateCity = async () => {
    if (!editCity) return;
    try {
      await axios.put(`${API}/superadmin/cities/${editCity.id}`, {
        name: editCity.name,
        state: editCity.state,
        country: editCity.country,
        is_active: editCity.is_active
      });
      toast.success("City updated successfully");
      setEditCity(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update city");
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (!confirm("Are you sure you want to delete this city?")) return;
    try {
      await axios.delete(`${API}/superadmin/cities/${cityId}`);
      toast.success("City deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete city");
    }
  };

  const handleOpenAssignDialog = async (admin) => {
    try {
      const res = await axios.get(`${API}/superadmin/admin/${admin.id}/cities`);
      setAssignDialog({ open: true, admin, selectedCities: res.data.city_ids || [] });
    } catch (err) {
      toast.error("Failed to load admin cities");
    }
  };

  const handleAssignCities = async () => {
    try {
      await axios.put(`${API}/superadmin/admin/${assignDialog.admin.id}/cities`, {
        admin_id: assignDialog.admin.id,
        city_ids: assignDialog.selectedCities
      });
      toast.success("Cities assigned successfully");
      setAssignDialog({ open: false, admin: null, selectedCities: [] });
    } catch (err) {
      toast.error("Failed to assign cities");
    }
  };

  const toggleCitySelection = (cityId) => {
    setAssignDialog(prev => ({
      ...prev,
      selectedCities: prev.selectedCities.includes(cityId)
        ? prev.selectedCities.filter(id => id !== cityId)
        : [...prev.selectedCities, cityId]
    }));
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="cities-page">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">City Management</h2>
          <p className="text-muted-foreground">Manage cities and assign them to admins</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDuplicates} disabled={loadingDuplicates}>
            {loadingDuplicates ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Check Duplicates
          </Button>
          <Button onClick={() => setShowAddCity(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add City
          </Button>
        </div>
      </div>

      {/* Duplicates Section */}
      {showDuplicates && duplicates.length > 0 && (
        <Card className="mb-6 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                  <AlertTriangle className="w-5 h-5" />
                  Duplicate Cities Found ({duplicates.length} groups)
                </CardTitle>
                <CardDescription>
                  These cities have the same name. Merge them to avoid confusion with orders and drivers.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="default" 
                  onClick={handleMergeAll}
                  disabled={merging !== null}
                >
                  {merging === 'all' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
                  Merge All
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowDuplicates(false)}>
                  Hide
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {duplicates.map((dup) => (
                <div key={dup._id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border">
                  <div className="flex-1">
                    <p className="font-medium capitalize">{dup._id}</p>
                    <p className="text-sm text-muted-foreground">{dup.count} cities with this name</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {dup.cities.map((c, idx) => (
                        <Badge key={c.id} variant={idx === 0 ? "default" : "secondary"} className="text-xs">
                          {idx === 0 ? "Keep: " : ""}{new Date(c.created_at).toLocaleDateString()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleMergeCities(dup)}
                    disabled={merging !== null}
                  >
                    {merging === dup._id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Merge className="w-4 h-4 mr-1" />}
                    Merge
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Duplicates Message */}
      {showDuplicates && duplicates.length === 0 && !loadingDuplicates && (
        <Card className="mb-6 border-green-500 bg-green-50 dark:bg-green-900/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-700 dark:text-green-400">No Duplicate Cities</p>
                <p className="text-sm text-muted-foreground">All city names are unique.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile Cards View for Cities */}
      <div className="md:hidden space-y-3 mb-6">
        <h3 className="font-semibold text-foreground">All Cities ({cities.length})</h3>
        {cities.length === 0 ? (
          <div className="bg-card rounded-xl border border-border p-6 text-center text-muted-foreground">No cities added yet</div>
        ) : (
          cities.map((city) => (
            <div key={city.id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{city.name}</p>
                    <p className="text-xs text-muted-foreground">{city.state}</p>
                    <p className="text-xs text-muted-foreground">Owner: {getOwnerName(city.owner_admin_id)}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${city.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                  {city.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditCity(city)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOwnerDialog({ open: true, city, newOwnerId: city.owner_admin_id || '' })}>
                  <UserCog className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteCity(city.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Mobile Cards View for Admin Assignments */}
      <div className="md:hidden space-y-3 mb-6">
        <h3 className="font-semibold text-foreground">Admin City Assignments</h3>
        {admins.map((admin) => (
          <div key={admin.id} className="bg-card rounded-xl border border-border p-4">
            <div className="mb-3">
              <p className="font-medium">{admin.name}</p>
              <p className="text-xs text-muted-foreground">{admin.email}</p>
            </div>
            <Button size="sm" variant="outline" className="w-full" onClick={() => handleOpenAssignDialog(admin)}>
              Assign Cities
            </Button>
          </div>
        ))}
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:grid md:grid-cols-2 gap-6">
        {/* Cities Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold">All Cities ({cities.length})</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>City</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cities.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No cities added yet</TableCell></TableRow>
              ) : (
                cities.map((city) => (
                  <TableRow key={city.id}>
                    <TableCell className="font-medium">{city.name}</TableCell>
                    <TableCell>{city.state}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {getOwnerName(city.owner_admin_id)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${city.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {city.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditCity(city)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => setOwnerDialog({ open: true, city, newOwnerId: city.owner_admin_id || '' })}>
                          <UserCog className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCity(city.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Assign Cities to Admins */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold">Admin City Assignments</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admin</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div><p className="font-medium">{admin.name}</p><p className="text-xs text-muted-foreground">{admin.email}</p></div>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => handleOpenAssignDialog(admin)}>Assign Cities</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add City Dialog */}
      <Dialog open={showAddCity} onOpenChange={setShowAddCity}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New City</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>City Name</Label><Input value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} placeholder="e.g., Bangalore" className="mt-1" /></div>
            <div><Label>State</Label><Input value={newCity.state} onChange={(e) => setNewCity({ ...newCity, state: e.target.value })} placeholder="e.g., Karnataka" className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={newCity.is_active} onCheckedChange={(checked) => setNewCity({ ...newCity, is_active: checked })} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCity(false)}>Cancel</Button>
            <Button onClick={handleAddCity}>Add City</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit City Dialog */}
      <Dialog open={!!editCity} onOpenChange={(open) => !open && setEditCity(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit City</DialogTitle></DialogHeader>
          {editCity && (
            <div className="space-y-4 py-4">
              <div><Label>City Name</Label><Input value={editCity.name} onChange={(e) => setEditCity({ ...editCity, name: e.target.value })} className="mt-1" /></div>
              <div><Label>State</Label><Input value={editCity.state} onChange={(e) => setEditCity({ ...editCity, state: e.target.value })} className="mt-1" /></div>
              <div className="flex items-center gap-2"><Switch checked={editCity.is_active} onCheckedChange={(checked) => setEditCity({ ...editCity, is_active: checked })} /><Label>Active</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCity(null)}>Cancel</Button>
            <Button onClick={handleUpdateCity}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Cities Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => !open && setAssignDialog({ open: false, admin: null, selectedCities: [] })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Cities to {assignDialog.admin?.name}</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">Select cities this admin can manage:</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {cities.filter(c => c.is_active).map((city) => (
                <div key={city.id} className={`p-3 rounded-lg border cursor-pointer transition-all ${assignDialog.selectedCities.includes(city.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} onClick={() => toggleCitySelection(city.id)}>
                  <div className="flex items-center justify-between">
                    <div><p className="font-medium">{city.name}</p><p className="text-xs text-muted-foreground">{city.state}</p></div>
                    {assignDialog.selectedCities.includes(city.id) && <CheckCircle className="w-5 h-5 text-primary" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, admin: null, selectedCities: [] })}>Cancel</Button>
            <Button onClick={handleAssignCities}>Assign ({assignDialog.selectedCities.length} cities)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Owner Dialog */}
      <Dialog open={ownerDialog.open} onOpenChange={(open) => !open && setOwnerDialog({ open: false, city: null, newOwnerId: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change City Owner</DialogTitle>
          </DialogHeader>
          {ownerDialog.city && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{ownerDialog.city.name}</p>
                <p className="text-sm text-muted-foreground">{ownerDialog.city.state}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Current Owner: <span className="font-medium">{getOwnerName(ownerDialog.city.owner_admin_id)}</span>
                </p>
              </div>
              <div>
                <Label>New Owner</Label>
                <Select value={ownerDialog.newOwnerId} onValueChange={(value) => setOwnerDialog(prev => ({ ...prev, newOwnerId: value }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select new owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Owner</SelectItem>
                    {allAdminsForOwnership.map((admin) => (
                      <SelectItem key={admin.id} value={admin.id}>
                        {admin.name} ({admin.phone || admin.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOwnerDialog({ open: false, city: null, newOwnerId: '' })}>Cancel</Button>
            <Button onClick={handleChangeOwner}>Change Owner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CitiesPage;
